import { useState, useEffect, useRef } from 'react'
import { UUID } from "uuidjs";
import useClient from './useClient'
import cloneDeep from 'lodash.clonedeep'
import { OrderStates, OrderStatus } from '../orderengine-client/index'
import { localStorage } from 'okeoke.localstorage'
import okeResponse from 'okeoke.standardresponse';

export default function useOrder(
  _locationUUID = null,
  _orderUUID = null
) {
  const [orderItems, setOrderItems] = useState([])
  const [orderItemsAssembled, setOrderItemsAssembled] = useState(null)
  const [orderUUID, setOrderUUID] = useState(null)
  const [order, setOrder] = useState(null)
  const orderRef = useRef(null)
  const [error, setError] = useState(false)
  const uid = useRef(UUID.generate())
  const _prevLocationUUID = useRef(null)
  const [locationUUID, setLocationUUID] = useState(null)

  const [orderState, setOrderState] = useState(null)
  const [states, setStates] = useState(null)

  const [totalPrice, setTotalPrice] = useState(0)
  const [discountPrice, setDiscountPrice] = useState(0)

  const [sumPrice, setSumPrice] = useState(0)
  const [sumDiscountPrice, setSumDiscountPrice] = useState(0)
  const [packagingPrice, setPackagingPrice] = useState(0)

  const [discountAmount, setDiscountAmount] = useState(0)
  const [vatAmounts, setVatAmounts] = useState(0)
  const [paymentRounding, setPaymentRounding] = useState(0)
  const [packagingDiscountPrice, setPackagingDiscountPrice] = useState(0)
  const [sumServiceFee, setSumServiceFee] = useState(0)
  const [sumDiscountServiceFee, setSumDiscountServiceFee] = useState(0)

  const [depositPrice, setDepositPrice] = useState(0)

  const [paymentsNeeded, setPaymentsNeeded] = useState(null)
  const [paymentsTotal, setPaymentsTotal] = useState(null)
  const [paymentState, setPaymentState] = useState({ paymentInProgress: false })
  const [paymentType, setPaymentTypeState] = useState(null)

  const [tipAmount, setTipAmountState] = useState(null)
  const [tippType, setTippTypeState] = useState(0)

  const [takeAway, setTakeAwayState] = useState(false)
  const [itemCount, setItemCount] = useState(0)

  const [payments, setPayments] = useState([])
  const [invoices, setInvoices] = useState([])
  const [statuses, setStatuses] = useState([])

  const [invoiceNeeded, setInvoiceNeededState] = useState(0)
  const [invoiceInfo, setInvoiceInfoState] = useState(null)
  const [deliveryInfo, setDeliveryInfoState] = useState(null)
  const [deliveryType, setDeliveryTypeState] = useState(null)
  const [deliveryCost, setDeliveryCostState] = useState(null)
  const [deliveryPayment, setDeliveryPaymentState] = useState(null)

  const [requestedReadyTime, setRequestedReadyTimeState] = useState(null)
  const [tableInfo, setTableInfoState] = useState(null)
  const [tableName, setTableNameState] = useState(null)
  const [tableUUID, setTableUUIDState] = useState(null)
  const [location, setLocationState] = useState(null)

  const [notes, setNotesState] = useState(null)

  const [orderNumber, setOrderNumberState] = useState(null)
  const [orderIdentifier, setOrderIdentifierState] = useState(null)

  const [vipPoints, setVipPointsState] = useState(0)
  const [coupons, setCouponsState] = useState(null)
  const [localDiscounts, setLocalDiscounts] = useState(null)

  const [serviceFee, setServiceFeeState] = useState(null)

  const [vipID, setVipIDState] = useState(null)

  const [sandbox, setSandboxState] = useState(false)

  const [orderType, setOrderTypeState] = useState(0)
  const [personnelUUID, setPersonnelUUID] = useState(null)
  const [consumerUUID, setConsumerUUID] = useState(null)

  const [isReady, setIsReady] = useState(false)

  const {
    ordersInited,
    client,
    deviceLocationUUID,
    selectedLocationUUID,
    setActiveBasket,
    activeBasketUUID,
    sandboxMode
  } = useClient()

  const onOrderUpdated = (data) => {
    if (orderUUID != null && orderUUID === data?.order.uuid) {
      setLocationUUID(data?.order?.locationUUID)
      setVipPointsState(data?.order?.calculated?.discounts?.vip?.total || 0 || 0)
      setCouponsState(data?.order?.calculated?.discounts?.coupons || null)
      setLocalDiscounts(data?.order?.calculated?.discounts?.local || null)

      setServiceFeeState(cloneDeep(data?.order?.calculated?.serviceFee))

      setOrderState(data?.order?.orderState || 0)
      setStates(data?.order?.states || {})
      setOrderNumberState(data?.order?.orderNumber || null)
      setOrderIdentifierState(data?.order?.orderIdentifier || '')

      setTakeAwayState(data?.order?.takeAway || 0)
      setItemCount(data?.order?.itemCount || 0)

      setOrderItems(data?.order?.items ? cloneDeep(data?.order?.items) : [])
      setOrderItemsAssembled(data?.assembledOrderItems ? cloneDeep(data?.assembledOrderItems) : {})

      setPayments(data?.order?.payments ? cloneDeep(data?.order?.payments) : [])
      setInvoices(data?.order?.invoices ? cloneDeep(data?.order?.invoices) : [])
      setStatuses(data?.order?.statuses ? cloneDeep(data?.order?.statuses) : [])

      setRequestedReadyTimeState(data?.order?.requestedReadyTime)
      setTableInfoState(data?.order?.tableInfo ? cloneDeep(data?.order?.tableInfo) : null)
      setTableNameState(data?.order?.tableInfo ? data?.order?.tableInfo.name : null)
      setTableUUIDState(data?.order?.tableUUID || null)
      setLocationState(data?.order?.location ? { ...data?.order?.location } : null)
      setNotesState(data?.order?.notes || null)

      setInvoiceNeededState(data?.order?.invoiceNeeded ? 1 : 0)
      setInvoiceInfoState(data?.order?.invoiceInfo)
      setDeliveryInfoState(data?.order?.deliveryInfo)
      setDeliveryTypeState(data?.order?.deliveryType)
      setDeliveryCostState(data?.order?.deliveryCost)
      setDeliveryPaymentState(data?.order?.deliveryPayment)

      setTotalPrice(data?.order?.totalPrice || 0)
      setDiscountPrice(data?.order?.discountPrice)

      setDiscountAmount(data?.order?.discountAmount || 0)
      setVatAmounts(data?.order?.vatAmounts || null)
      setPaymentRounding(data?.order?.paymentRounding || 0)
      setPackagingDiscountPrice(data?.order?.packagingDiscountPrice || 0)
      setSumServiceFee(data?.order?.sumServiceFee || 0)
      setSumDiscountServiceFee(data?.order?.sumDiscountServiceFee || 0)

      setSumPrice(data?.order?.sumPrice || 0)
      setSumDiscountPrice(data?.order?.sumDiscountPrice || 0)

      setPackagingPrice(data?.order?.packagingPrice || 0)

      setDepositPrice(data?.order?.depositPrice || 0)

      setPaymentsNeeded(data?.order?.paymentsNeeded)
      setPaymentsTotal(data?.order?.paymentsTotal)

      setTipAmountState(data?.order?.tipAmount)
      setTippTypeState(data?.order?.tippType)

      setPaymentState(data?.order?.paymentState)
      setPaymentTypeState(data?.order?.paymentType)

      setVipIDState(data?.order?.vipID)

      setSandboxState(data?.order?.sandbox || false)
      setOrderTypeState(data?.order?.orderType || 0)
      setPersonnelUUID(data?.order?.personnelUUID || null)
      setConsumerUUID(data?.order?.consumerUUID || null)

      if (data.order.orderState !== OrderStates.BASKET && data.order.uuid === activeBasketUUID) {
        setActiveBasket(null)
      }

      if (!isReady) setIsReady(true)
    }
  }

  useEffect(() => {
    // Check if order was submitted, if so, dont change anything
    if (orderState === 0) {
      if (deviceLocationUUID != null) {
        setLocationUUID(deviceLocationUUID)
      } else {
        if (selectedLocationUUID != null || _locationUUID != null) {
          let newLocationUUID = _locationUUID != null ? _locationUUID : selectedLocationUUID
          if (newLocationUUID != null && newLocationUUID != _prevLocationUUID.current) {
            if (orderRef.current != null) {
              //console.log('SET ORDER LOCATION2', newLocationUUID)
              orderRef.current.general.setLocation(newLocationUUID)
              setLocationUUID(newLocationUUID)
            }
          }
        }
      }
    }
  }, [_locationUUID, deviceLocationUUID, selectedLocationUUID, orderState])

  useEffect(() => {
    _prevLocationUUID.current = locationUUID
  }, [locationUUID])

  useEffect(() => {
    orderRef.current = order
    if (order != null) {
      setVipPointsState(order?.order?.calculated?.discounts?.vip?.total || 0)
      setCouponsState(order?.order?.calculated?.discounts?.coupons || null)
      setLocalDiscounts(order?.order?.calculated?.discounts?.local || null)

      setServiceFeeState(cloneDeep(order?.order?.calculated?.serviceFee))

      setLocationUUID(order?.order?.locationUUID)

      setOrderItems(order?.order?.items ? cloneDeep(order?.order?.items) : [])
      setOrderItemsAssembled(order?.assembledOrderItems ? cloneDeep(order?.assembledOrderItems) : {})
      setOrderState(order?.order?.orderState || 0)
      setStates(order?.order?.states || {})
      setOrderNumberState(order?.order?.orderNumber || null)
      setOrderIdentifierState(order?.order?.orderIdentifier || '')

      setTotalPrice(order?.order?.totalPrice || 0)
      setDiscountPrice(order?.order?.discountPrice)

      setDiscountAmount(order?.order?.discountAmount || 0)
      setVatAmounts(order?.order?.vatAmounts || null)
      setPaymentRounding(order?.order?.paymentRounding || 0)
      setPackagingDiscountPrice(order?.order?.packagingDiscountPrice || 0)
      setSumServiceFee(order?.order?.sumServiceFee || 0)
      setSumDiscountServiceFee(order?.order?.sumDiscountServiceFee || 0)

      setSumPrice(order?.order?.sumPrice || 0)
      setSumDiscountPrice(order?.order?.sumDiscountPrice || 0)
      setPackagingPrice(order?.order?.packagingPrice || 0)

      setDepositPrice(order?.order?.depositPrice || 0)

      setPaymentsNeeded(order?.order?.paymentsNeeded)
      setPaymentsTotal(order?.order?.paymentsTotal)

      setTipAmountState(order?.order?.tipAmount)
      setTippTypeState(order?.order?.tippType)

      setPaymentState(order?.order?.paymentState)
      setPaymentTypeState(order?.order?.paymentType)

      setVipIDState(order?.order?.vipID)

      setTakeAwayState(order?.order?.takeAway || 0)
      setItemCount(order?.order?.itemCount || 0)

      setPayments(order?.order?.payments ? cloneDeep(order?.order?.payments) : [])
      setInvoices(order?.order?.invoices ? cloneDeep(order?.order?.invoices) : [])
      setStatuses(order?.order?.statuses ? cloneDeep(order?.order?.statuses) : [])

      setRequestedReadyTimeState(order?.order?.requestedReadyTime)
      setTableInfoState(order?.order?.tableInfo ? cloneDeep(order?.order?.tableInfo) : null)
      setTableNameState(order?.order?.tableInfo ? order?.order?.tableInfo.name : null)
      setTableUUIDState(order?.order?.tableUUID || null)
      setLocationState(order?.order?.location ? { ...order?.order?.location } : null)
      setNotesState(order?.order?.notes || null)

      setInvoiceNeededState(order?.order?.invoiceNeeded)
      setInvoiceInfoState(cloneDeep(order?.order?.invoiceInfo))
      setDeliveryInfoState(cloneDeep(order?.order?.deliveryInfo))
      setDeliveryTypeState(order?.order?.deliveryType)
      setDeliveryCostState(order?.order?.deliveryCost)
      setDeliveryPaymentState(order?.order?.deliveryPayment)

      setSandboxState(order?.order?.sandbox || false)
      setOrderTypeState(order?.order?.orderType || 0)
      setPersonnelUUID(order?.order?.personnelUUID || null)
      setConsumerUUID(order?.order?.consumerUUID || null)

      if (!isReady) setIsReady(true)
    } else {
      setVipPointsState(0)
      setCouponsState(null)
      setLocalDiscounts(null)

      setServiceFeeState(null)

      setLocationUUID(null)

      setOrderItems([])
      setOrderItemsAssembled(null)
      setOrderState(0)
      setStates(null)
      setOrderNumberState(null)
      setOrderIdentifierState('')

      setTotalPrice(0)
      setDiscountPrice(0)

      setDiscountAmount(0)
      setVatAmounts(null)
      setPaymentRounding(0)
      setPackagingDiscountPrice(0)
      setSumServiceFee(0)
      setSumDiscountServiceFee(0)

      setSumPrice(0)
      setSumDiscountPrice(0)
      setPackagingPrice(0)

      setDepositPrice(0)

      setPaymentsNeeded(0)
      setPaymentsTotal(0)

      setTipAmountState(0)
      setTippTypeState(0)

      setPaymentState({ paymentInProgress: false })
      setPaymentTypeState(null)

      setVipIDState(null)

      setTakeAwayState(0)
      setItemCount(0)

      setPayments([])
      setInvoices([])
      setStatuses([])

      setRequestedReadyTimeState(null)
      setTableInfoState(null)
      setTableNameState(null)
      setTableUUIDState(null)
      setLocationState(null)
      setNotesState(null)

      setInvoiceNeededState(null)
      setInvoiceInfoState(null)
      setDeliveryInfoState(null)
      setDeliveryTypeState(null)
      setDeliveryCostState(null)
      setDeliveryPaymentState(null)
      setSandboxState(false)
    }
  }, [order])

  useEffect(() => {
    if (ordersInited && client != null) {
      if (orderUUID != null) {
        client.orders.loadOrder(orderUUID).then((_order) => {
          if (_order != null) {
            setOrder(_order)
            setError(false)
          } else {
            setError(true)
          }
        })
      } else {
        setOrder(null)
      }
    }
  }, [client, ordersInited, orderUUID])

  useEffect(() => {
    if (_orderUUID != null && _orderUUID !== '') {
      setOrderUUID(_orderUUID)
    } else {
      if (activeBasketUUID != null && _orderUUID == null) {
        setOrderUUID(activeBasketUUID)
      }
    }
  }, [_orderUUID, activeBasketUUID])

  useEffect(() => {
    if (client && ordersInited) {
      client.orders.addCallback('onOrderUpdated', uid.current, onOrderUpdated)
    }
    return () => {
      // ON UNMOUNT
      if (client != null) {
        client.orders.removeCallback('onOrderUpdated', uid.current)
      }
    }
  }, [ordersInited, client, orderUUID])

  const startNewOrder = (params = {}, getLastBasket = false) => {
    let tableUUID = localStorage.getItem('oe-stuuid')
    tableUUID = tableUUID?.length === 32 ? tableUUID : null
    localStorage.removeItem('oe-stuuid')
    let { locationUUID: locUUIDParam = null, tableUUID: tableUUIDParam = null } = params
    locUUIDParam = locUUIDParam || deviceLocationUUID || selectedLocationUUID
    tableUUIDParam = tableUUIDParam || tableUUID || null
    let newOrder = client.orders.newOrder({ ...params, locationUUID: locUUIDParam, tableUUID: tableUUIDParam, sandbox: (sandboxMode ? true : false) }, getLastBasket)
    if (newOrder.success) {
      setOrderUUID(newOrder.data.order.uuid)
      setActiveBasket(newOrder.data.order.uuid)
      return newOrder.data
    } else {
      return null
    }
  }

  const addItem = (newItem, parentOrderItemUUID = null) => {
    let item = cloneDeep(newItem)
    if (orderRef.current != null) {
      if (orderRef.current.order?.orderState === OrderStates.BASKET) {
        delete item.orderItemUUID
        if (item.count == null) item.count = 1
        let uuid = orderRef.current.updateBasketItem(item, null, parentOrderItemUUID)
        return uuid
      } else {
        return null
      }
    } else {
      // Create new order and add the item
      if (activeBasketUUID != null) {
        setOrderUUID(activeBasketUUID)
      }
      let newOrder = startNewOrder({}, true)
      if (newOrder == null) return null
      delete item.orderItemUUID
      if (item.count == null) item.count = 1
      let addItm = newOrder?.updateBasketItem(item)
      return addItm
    }
  }

  const updateItem = (uItem) => {
    let item = cloneDeep(uItem)
    if (orderRef.current != null) {
      if (orderRef.current.order?.orderState === OrderStates.BASKET) {
        return orderRef?.current?.updateBasketItem(item)
      } else {
        //console.log('Can not update item in non basket order')
        return null
      }
    }
  }

  const updateItemCount = (orderItemUUID, count) => {
    if (orderRef.current != null) {
      return orderRef?.current?.updateBasketItemCount(orderItemUUID, count)
    }
  }

  const updateItemQuantity = (orderItemUUID, quantity) => {
    if (orderRef.current != null) {
      //console.log(quantity)
      return orderRef?.current?.updateBasketItemQuantity(orderItemUUID, quantity)
    }
  }

  const updateItemSize = (orderItemUUID, uuid) => {
    if (orderRef.current != null) {
      return orderRef?.current?.updateBasketItemSize(orderItemUUID, uuid)
    }
  }

  const setTakeAway = (takeAway) => {
    if (orderRef.current != null) {
      orderRef.current.general.setTakeAway(takeAway)
    }
  }

  const setOrderType = (orderType, personnelUUID, consumerUUID) => {
    if (orderRef.current != null) {
      orderRef.current.general.setOrderType(orderType, personnelUUID, consumerUUID)
    }
  }

  const setLocation = (__locationUUID) => {
    if (__locationUUID !== _prevLocationUUID.current) {
      setLocationUUID(__locationUUID)
      if (orderRef.current != null) {
        //console.log('SET ORDER LOCATION1', locationUUID)
        orderRef.current.general.setLocation(__locationUUID)
      }
    }
  }

  const setLanguage = (code) => {
    if (orderRef.current != null) {
      orderRef.current.general.setLanguage(code)
    }
  }

  const setDeliveryInfo = (info, type, cost) => {
    if (orderRef.current != null) {
      orderRef.current.general.setDeliveryInfo(info, type, cost)
    }
  }

  const setServiceFeePercent = (percent) => {
    if (orderRef.current != null) {
      orderRef.current.general.setServiceFeePercent(percent)
    }
  }

  const setInvoiceInfo = (info) => {
    if (orderRef.current != null) {
      orderRef.current.general.setInvoiceInfo(info)
    }
  }

  const setInvoiceNeeded = (needed) => {
    if (orderRef.current != null) {
      orderRef.current.general.setInvoiceNeeded(needed)
    }
  }

  const setNotes = (notes) => {
    if (orderRef.current != null) {
      orderRef.current.general.setNotes(notes)
    }
  }

  const setRequestedReadyTime = (readyTime) => {
    if (orderRef.current != null) {
      orderRef.current.general.setRequestedReadyTime(readyTime)
    }
  }

  const setPaymentType = (paymentType) => {
    if (orderRef.current != null) {
      orderRef.current.general.setPaymentType(paymentType)
    }
  }

  const setVipID = (vipID) => {
    if (orderRef.current != null) {
      orderRef.current.vip.setVipID(vipID)
    }
  }

  const setUserID = (uuid) => {
    if (orderRef.current != null) {
      orderRef.current.setUserID(uuid)
    }
  }

  const setTable = (uuid) => {
    if (orderRef.current != null) {
      orderRef.current.general.setTable(uuid)
    }
  }

  const setGroup = (uuid) => {
    if (orderRef.current != null) {
      orderRef.current.general.setGroup(uuid)
    }
  }

  const setOrderIdentifier = (identifier) => {
    if (orderRef.current != null) {
      orderRef.current.general.setOrderIdentifier(identifier)
    }
  }

  const setTipAmount = (amount) => {
    if (orderRef.current != null) {
      orderRef.current.general.setTipAmount(amount)
    }
  }

  const reserveVip = async () => {
    if (orderRef.current != null) {
      let res = await orderRef.current.vip.reserveVip()
      //console.log('reserveVip: ', res)
      return res
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const finalizeVip = async () => {
    if (orderRef.current != null) {
      let res = await orderRef.current.vip.finalizeVip()
      //console.log('finalizeVip: ', res)
      return res
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const addStatus = async (statusID, notes) => {
    if (orderRef.current != null) {
      let res = await orderRef.current.statuses.addStatus(statusID, notes)
      //console.log('addStatus: ', res)
      return res
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const addItemStatus = async (orderItemUUID, statusID, notes) => {
    if (orderRef.current != null) {
      let res = await orderRef.current.statuses.addItemStatus(orderItemUUID, statusID, notes)
      //console.log('addItemStatus: ', res)
      return res
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const addPayment = async (paymentAgentID, amount, extTransactionID, info, stornoPaymentUUID = null, agentType = 'cash', agentSubType = null) => {
    if (orderRef.current != null) {
      if (amount <= 0 && stornoPaymentUUID == null) return { success: false, message: 'Invalid amount' }
      if (stornoPaymentUUID != null) {
        let stornoPayment = await orderRef.current.payments.getPayment('', '', stornoPaymentUUID)
        if (stornoPayment == null) {
          return { success: false, message: 'Payment to storno not found' }
        }
        if (stornoPayment.deleted) {
          return { success: false, message: 'Payment already deleted' }
        }
        if (stornoPayment.stornoPaymentUUID != null) {
          return { success: false, message: 'The id provided is of a storno' }
        }
        amount = -stornoPayment.amount

      }
      let res = await orderRef.current.payments.addPayment(paymentAgentID, amount, extTransactionID, info, stornoPaymentUUID, agentType, agentSubType)
      //console.log('addPayment: ', res)
      return res
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const addInvoice = async (invoiceAgentID, amount, extID, vatInvoice, info) => {
    if (orderRef.current != null) {
      //let res = await orderRef.current.invoices.addInvoice(invoiceAgentID, amount, extID, vatInvoice, info)
      //console.log('addInvoice: ', res)
      //return res
      return { success: false, message: 'Deprecated, handled by printReceipt' }
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const abort = async (reasonCode, reasonMessage) => {
    if (orderRef.current != null) {
      let res = await orderRef.current.abort(reasonCode, reasonMessage)
      //console.log('abort: ', res)
      return res
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const submit = async (paymentType = null) => {
    if (orderRef.current != null) {
      let res = await orderRef.current.submit(paymentType)
      //console.log('submit: ', res)
      return res
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const getOrderUpsales = (maxSuggestions = 3, isVisible = true) => {
    if (orderRef.current != null) {
      return orderRef.current.general.getOrderUpsales(maxSuggestions, isVisible)
    } else {
      return { success: false, message: 'No order opened' }
    }
  }

  const setItemDiscount = (discountID, _orderItemUUID, count = null, remainingAmount = 0) => {
    if (orderRef.current != null) {
      let res = orderRef.current.itemDiscounts.setItemDiscount(_orderItemUUID, discountID, count, false, false, false, remainingAmount)
      return res
    }
    return okeResponse(false, 404, 'Order not found.', { errMsgCode: 'order.notfound' })
  }

  const setDiscount = (_discountID, _orderItems) => {
    if (orderRef.current != null) {
      let orderItemUUIDs = _orderItems != null ? _orderItems.map((item) => item.orderItemUUID) : null
      let res = orderRef.current.itemDiscounts.setDiscountItems(_discountID, orderItemUUIDs)
      return res
    }
    return okeResponse(false, 404, 'Order not found.', { errMsgCode: 'order.notfound' })
  }

  const unsetDiscount = (discountID) => {
    if (orderRef.current != null) {
      let res = orderRef.current.itemDiscounts.unsetDiscountItems(discountID)
      return res
    }
    return okeResponse(false, 404, 'Order not found.', { errMsgCode: 'order.notfound' })
  }

  return {
    setDiscount,
    unsetDiscount,
    setItemDiscount,
    orderItems,
    orderItemsAssembled,
    order,
    orderUUID,
    setTakeAway,
    setLanguage,
    setDeliveryInfo,
    setServiceFeePercent,
    setInvoiceNeeded,
    setInvoiceInfo,
    setNotes,
    setRequestedReadyTime,
    setPaymentType,
    setVipID,
    setUserID,
    setTable,
    setGroup,
    setOrderIdentifier,
    setTipAmount,
    reserveVip,
    finalizeVip,
    getOrderUpsales,
    setLocation,
    addItem,
    updateItem,
    error,
    orderState,
    states,
    totalPrice,
    discountPrice,
    sumPrice,
    sumDiscountPrice,
    packagingPrice,
    depositPrice,
    paymentsNeeded,
    paymentsTotal,
    paymentState,
    takeAway,
    itemCount,
    startNewOrder,
    addStatus,
    addItemStatus,
    addPayment,
    addInvoice,
    abort,
    submit,
    payments,
    invoices,
    statuses,
    invoiceInfo,
    invoiceNeeded,
    deliveryInfo,
    deliveryCost,
    deliveryType,
    deliveryPayment,
    requestedReadyTime,
    tableUUID,
    tableInfo,
    tableName,
    location,
    orderNumber,
    orderIdentifier,
    notes,
    locationUUID,
    vipPoints,
    coupons,
    vipID,
    paymentType,
    isReady,
    localDiscounts,
    serviceFee,
    tipAmount,
    tippType,
    discountAmount,
    vatAmounts,
    paymentRounding,
    packagingDiscountPrice,
    sumServiceFee,
    sumDiscountServiceFee,
    sandbox,
    orderType,
    setOrderType,
    personnelUUID,
    consumerUUID,
    updateItemCount,
    updateItemQuantity,
    updateItemSize,
    OrderStatus
  }
}
